/**
 * Pure utility functions for PMS filtering and validation logic.
 * These are extracted for testability and reuse across route wrappers.
 */

import { canonicalizeEmail } from '../services/identity';
import {
  Objective,
  ObjectiveStatus,
  ReviewCycle,
  ReviewCycleStatus,
  PMSRole,
} from '../types/pms';

/**
 * Valid status transitions state machine.
 * Draft → Submitted → Approved/Returned → In_Progress → Completed
 */
const VALID_TRANSITIONS: Record<ObjectiveStatus, ObjectiveStatus[]> = {
  [ObjectiveStatus.DRAFT]: [ObjectiveStatus.SUBMITTED],
  [ObjectiveStatus.SUBMITTED]: [ObjectiveStatus.APPROVED, ObjectiveStatus.RETURNED],
  [ObjectiveStatus.APPROVED]: [ObjectiveStatus.IN_PROGRESS],
  [ObjectiveStatus.RETURNED]: [ObjectiveStatus.SUBMITTED],
  [ObjectiveStatus.IN_PROGRESS]: [ObjectiveStatus.COMPLETED],
  [ObjectiveStatus.UNDER_REVIEW]: [ObjectiveStatus.COMPLETED],
  [ObjectiveStatus.COMPLETED]: [],
};

/** Allowed file extensions for upload */
const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx'];

/** Maximum file size: 10 MB in bytes */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Filter objectives visible to a manager.
 * Returns objectives where managerId matches AND status is NOT Draft.
 * Validates: Requirements 1.3, 3.1, 13.3
 */
export function filterObjectivesForManager(
  objectives: Objective[],
  managerId: string
): Objective[] {
  return objectives.filter(
    (obj) => obj.managerId === managerId && obj.status !== ObjectiveStatus.DRAFT
  );
}

/**
 * Filter to active HR-created review cycles.
 * Annual parent cycles are configuration containers; employee dropdowns should
 * show the child review cycles created under them.
 */
export function filterActiveCycles(cycles: ReviewCycle[]): ReviewCycle[] {
  return cycles.filter(
    (cycle) => cycle.annualCycleId && cycle.status === ReviewCycleStatus.ACTIVE
  );
}

/** Calendar year of an ISO-ish date string, or undefined when it is absent/unparseable. */
function yearOf(date?: string): number | undefined {
  if (!date) return undefined;
  const year = new Date(date).getFullYear();
  return Number.isFinite(year) ? year : undefined;
}

/**
 * Whether a cycle's own date range covers the given year.
 *
 * An overlap test, not `startYear === year`: a fiscal cycle like "FY 2026-2027" legitimately
 * runs from one calendar year into the next, and an employee working in either of those years
 * needs to see it. A cycle with no usable dates is treated as covering the year — better to show
 * one extra cycle than to hide the one somebody needs because a date field was left blank.
 */
export function cycleCoversYear(cycle: ReviewCycle, year: number): boolean {
  const start = yearOf(cycle.startDate);
  const end = yearOf(cycle.endDate);
  if (start === undefined && end === undefined) return true;
  const from = start ?? (end as number);
  const to = end ?? (start as number);
  return from <= year && year <= to;
}

/**
 * The review cycles an employee should be offered: active children of the objective cycles that
 * belong to the current year.
 *
 * The dropdown previously showed every active child cycle in the system, which in practice meant
 * a flat list mixing this year's reviews with children of long-finished objective cycles — "Q1",
 * "end q1", "test review cycle" and so on all at once.
 *
 * The year is judged from the PARENT objective cycle, not from the child, because that is the
 * hierarchy the employee actually thinks in: an objective cycle belongs to a year, and its
 * reviews belong to it. Judging each child on its own dates would keep a stray review created
 * with this year's dates under a parent from three years ago.
 *
 * `referenceDate` is injected rather than read from the clock inside, so the current year is
 * never hardcoded and the rule stays testable.
 */
export function filterCurrentYearReviewCycles(
  cycles: ReviewCycle[],
  referenceDate: Date = new Date()
): ReviewCycle[] {
  const year = referenceDate.getFullYear();
  const byId = new Map(cycles.map((cycle) => [cycle.id, cycle]));

  return filterActiveCycles(cycles).filter((child) => {
    const parent = child.annualCycleId ? byId.get(child.annualCycleId) : undefined;
    // Orphaned child (parent row not in this fetch): fall back to its own dates rather than
    // dropping a cycle we cannot place.
    return cycleCoversYear(parent ?? child, year);
  });
}

/**
 * The objective cycle the organisation is CURRENTLY running, or undefined when none is.
 *
 * An annual objective cycle is a parent row — a ReviewCycle with no annualCycleId — and HR
 * publishes one by setting it Active. Annual Objective Cycles says as much on the page:
 * "An active cycle is currently published. Employees can create goals."
 *
 * Nothing in the schema stops two being Active at once, so the most recently STARTED one
 * wins. That is the one people are working in: an older cycle left Active is a cycle
 * somebody forgot to close, not the year everybody is being reviewed for.
 *
 * `referenceDate` is unused today and taken anyway, so a later rule that needs to exclude a
 * cycle whose dates have not begun can be added here rather than at the call sites.
 */
export function currentAnnualCycle(
  cycles: ReviewCycle[],
  referenceDate: Date = new Date()
): ReviewCycle | undefined {
  void referenceDate;
  const active = cycles.filter(
    (cycle) => !cycle.annualCycleId && cycle.status === ReviewCycleStatus.ACTIVE
  );
  if (active.length === 0) return undefined;

  return active.reduce((latest, cycle) =>
    String(cycle.startDate || '').localeCompare(String(latest.startDate || '')) > 0
      ? cycle
      : latest
  );
}

/**
 * The review cycles to offer an employee: the current objective cycle's, and only those.
 *
 * ── What this replaces, and why the year was not enough ─────────────────────────
 *
 * filterCurrentYearReviewCycles keeps every active child whose PARENT covers the current
 * calendar year. That was already a large improvement on showing every active child in the
 * system, and it is still far too loose: an organisation that has set up several objective
 * cycles this year — a real one and the trials that preceded it — has parents that all cover
 * this year, so all of them survive. The dropdown showed seven objective cycles at once,
 * each with its own reviews under it, and the employee had to know which was live.
 *
 * The year is not the question. The question is which objective cycle is RUNNING, and the
 * data already answers it: exactly one parent is published Active. So the list is its
 * children, and nothing else.
 *
 * ── Why it falls back rather than emptying ───────────────────────────────────
 *
 * With no Active parent there is no current cycle, and an empty dropdown would tell an
 * employee with a review open that they have none. The year rule is the honest answer there:
 * it is what this screen did before, and it is a wider list, not a wrong one.
 *
 * An Active parent with no active children also falls back, for the same reason — a cycle
 * published before its reviews were created must not blank a screen that had something on it.
 */
export function filterCurrentCycleReviewCycles(
  cycles: ReviewCycle[],
  referenceDate: Date = new Date()
): ReviewCycle[] {
  const current = currentAnnualCycle(cycles, referenceDate);
  if (!current) return filterCurrentYearReviewCycles(cycles, referenceDate);

  const mine = filterActiveCycles(cycles).filter(
    (child) => child.annualCycleId === current.id
  );
  return mine.length > 0 ? mine : filterCurrentYearReviewCycles(cycles, referenceDate);
}

/*
 * ─── Objectives per objective cycle ────────────────────────────────────────────────────────
 *
 * Objectives hang off the ANNUAL objective cycle, and a review cycle is a child of that. So one
 * objective is reviewed by Review 1, Review 2 and Review 3 and is still one objective. Anything
 * counting objectives has to key on the objective cycle and on the objective's own id.
 */

/**
 * The objective cycle an objective belongs to.
 *
 * `objectiveCycleId` is the field newer records carry; `reviewCycleId` is where older ones put
 * the same value, which is why both are consulted. Never use reviewCycleId as an objective's
 * IDENTITY — it says which cycle the objective is in, not which objective it is.
 */
export function getObjectiveCycleId(objective: Objective): string {
  return String((objective as any).objectiveCycleId || objective.reviewCycleId || '');
}

/**
 * Unique objectives belonging to one objective cycle, optionally scoped to a set of employees.
 *
 * Deduplicated by objective id because the fetch fans out per team member and per email-domain
 * alias, so the same row can legitimately arrive more than once — and a manager's headline count
 * must not move because of how many times a record was returned.
 *
 * An empty `cycleId` returns nothing rather than everything: a scoped count with nothing to
 * scope by is a failed lookup, not a request for the whole history.
 *
 * `employeeEmails` must be built with canonicalizeEmail so both domains collapse to one value
 * per person on each side of the comparison.
 */
export function uniqueObjectivesForCycle(
  objectives: Objective[],
  cycleId: string,
  employeeEmails?: Set<string>
): Objective[] {
  if (!cycleId) return [];
  const byId = new Map<string, Objective>();
  for (const objective of objectives) {
    if (getObjectiveCycleId(objective) !== cycleId) continue;
    // Canonicalised, not lowercased: an objective written before the domain move carries the
    // employee's old address while the roster reports the new one. See services/identity.ts.
    if (employeeEmails && !employeeEmails.has(canonicalizeEmail(objective.employeeId))) {
      continue;
    }
    if (objective.id && !byId.has(objective.id)) byId.set(objective.id, objective);
  }
  return Array.from(byId.values());
}

/**
 * Validate whether a status transition is allowed.
 * Validates: Requirements 2.4
 */
export function isValidStatusTransition(
  current: ObjectiveStatus,
  target: ObjectiveStatus
): boolean {
  const allowed = VALID_TRANSITIONS[current];
  if (!allowed) return false;
  return allowed.includes(target);
}

/**
 * Validate a file for upload (extension and size).
 * Returns { valid, error? }.
 * Validates: Requirements 10.4, 10.5, 10.6
 */
export function validateFileForUpload(file: {
  name: string;
  size: number;
}): { valid: boolean; error?: string } {
  // Extract extension from filename
  const parts = file.name.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';

  if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `File type ".${extension || 'unknown'}" is not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`,
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File size exceeds 10 MB limit`,
    };
  }

  return { valid: true };
}

/**
 * Check if a user has discussion access for an objective.
 * Access is granted if:
 * - User is the objective's employee (employeeId matches)
 * - User is the objective's manager (managerId matches)
 * - User has HR_Admin or Super_Admin role
 * Validates: Requirements 9.1, 9.2, 9.3
 */
export function hasDiscussionAccess(
  userId: string,
  userRole: PMSRole,
  objective: Objective
): boolean {
  // HR_Admin and Super_Admin always have access
  if (userRole === PMSRole.HR_ADMIN || userRole === PMSRole.SUPER_ADMIN) {
    return true;
  }

  // Employee owns the objective
  if (objective.employeeId === userId) {
    return true;
  }

  // Manager is assigned to the objective
  if (objective.managerId === userId) {
    return true;
  }

  return false;
}
